import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import HandleReplyHeader from './HandleReplyHeader';
import HandleReplyContactSection from './HandleReplyContactSection';
import HandleReplySuggestedReplySection from './HandleReplySuggestedReplySection';
import OpenDoorResearchSection from '../OpenDoor/OpenDoorResearchSection';
import { CHAR_LIMIT, FIELD_CHAR_LIMITS, INITIAL_CONTACT_FORM, buildMockSuggestedReply } from './constants';
import {
  canGenerateHandleReply,
  formToHandleReplyPayload,
  isAiUiMockEnabled,
  prospectToFormFields,
} from '../prospectUtils';
import { fetchProspectById, useCreateProspect, useUpdateProspect } from '../../../queries/prospects';

const createVersionId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getFieldLimit = (field) => FIELD_CHAR_LIMITS[field] ?? CHAR_LIMIT;

const ClientWorkspaceHandleReply = ({ companyName = 'this company', onBack }) => {
  const [form, setForm] = useState(INITIAL_CONTACT_FORM);
  const formRef = useRef(form);
  const [selectedProspectId, setSelectedProspectId] = useState(null);
  const selectedProspectIdRef = useRef(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);

  const { mutateAsync: createProspect, isPending: isCreating } = useCreateProspect();
  const { mutateAsync: updateProspect, isPending: isUpdating } = useUpdateProspect();

  const mockEnabled = isAiUiMockEnabled();
  const isGenerating = isCreating || isUpdating;

  const setFormState = (updater) => {
    setForm((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      formRef.current = next;
      return next;
    });
  };

  const setSelectedId = (id) => {
    selectedProspectIdRef.current = id;
    setSelectedProspectId(id);
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value ?? '';
    const limit = getFieldLimit(field);
    setFormState((prev) => ({
      ...prev,
      [field]: value.length <= limit ? value : value.slice(0, limit),
    }));
    if (field === 'fullName') {
      setSelectedId(null);
    }
  };

  const handleSelectProspect = async (prospect) => {
    if (!prospect?._id) return;

    // Optimistically populate from search hit, then reload full record by id.
    setSelectedId(prospect._id);
    setFormState((prev) => ({
      ...prev,
      ...prospectToFormFields(prospect),
      latestProspectReply: prev.latestProspectReply,
      communicationHistory: prev.communicationHistory,
    }));

    try {
      const full = await fetchProspectById(prospect._id);
      setSelectedId(full._id);
      setFormState((prev) => ({
        ...prev,
        ...prospectToFormFields(full),
        latestProspectReply: prev.latestProspectReply,
        communicationHistory: prev.communicationHistory,
      }));
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Failed to load prospect.';
      toast(message, { type: 'error' });
    }
  };

  const handleGenerate = async () => {
    const currentForm = formRef.current;
    const currentSelectedId = selectedProspectIdRef.current;

    if (!canGenerateHandleReply(currentForm)) {
      toast('Full name, company, and latest reply from prospect are required.', { type: 'warning' });
      return;
    }

    try {
      const payload = formToHandleReplyPayload(currentForm);
      const isUpdate = Boolean(currentSelectedId);
      const saved = isUpdate
        ? await updateProspect({ id: currentSelectedId, data: payload })
        : await createProspect({ data: payload });

      setSelectedId(saved._id);
      setFormState((prev) => ({
        ...prev,
        ...prospectToFormFields(saved),
        latestProspectReply: prev.latestProspectReply,
        communicationHistory: prev.communicationHistory,
      }));
      toast(isUpdate ? 'Prospect updated.' : 'Prospect saved.', { type: 'success' });

      // Production: Section 2 stays under construction until real AI is connected.
      // Local/dev only: populate Section 2 with clearly labeled mock content for UI testing.
      if (mockEnabled) {
        const mock = buildMockSuggestedReply({
          fullName: currentForm.fullName,
          company: currentForm.company,
          companyName,
        });
        const version = {
          id: createVersionId(),
          label: 'Suggested reply',
          body: mock.body,
          whyThisReply: mock.whyThisReply,
          createdAt: new Date(),
        };
        setVersions([version]);
        setActiveVersionId(version.id);
        setHasGenerated(true);
      } else {
        setVersions([]);
        setActiveVersionId(null);
        setHasGenerated(false);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save prospect.';
      toast(message, { type: 'error' });
    }
  };

  const handleRewrite = (instruction) => {
    if (!mockEnabled || !versions.length) return;

    const base = versions.find((version) => version.id === activeVersionId) || versions[0];
    const version = {
      id: createVersionId(),
      label: instruction.length > 28 ? `${instruction.slice(0, 28)}…` : instruction,
      body: `${base.body}\n\n—\nRefined for: ${instruction}`,
      whyThisReply: `This rewrite follows your request: “${instruction}”.`,
      createdAt: new Date(),
    };
    setVersions((prev) => [version, ...prev]);
    setActiveVersionId(version.id);
  };

  return (
    <Box display='flex' flexDirection='column' height='100%' minHeight={0} bgcolor='#fff'>
      <Box flex={1} overflow='auto' px={{ xs: 2.5, md: 4, xl: 5 }} pt={3} pb={4} minHeight={0}>
        <HandleReplyHeader companyName={companyName} onBack={onBack} />

        <Box
          display='grid'
          gridTemplateColumns={{
            xs: '1fr',
            md: 'minmax(260px, 1fr) minmax(300px, 2fr) minmax(260px, 1fr)',
          }}
          gap={2.5}
          alignItems='start'
        >
          <HandleReplyContactSection
            companyName={companyName}
            form={form}
            onFieldChange={handleFieldChange}
            onSelectProspect={handleSelectProspect}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            canGenerate={canGenerateHandleReply(form)}
          />
          <HandleReplySuggestedReplySection
            hasGenerated={hasGenerated}
            isMockMode={mockEnabled}
            versions={versions}
            activeVersionId={activeVersionId}
            onSelectVersion={setActiveVersionId}
            onRewrite={handleRewrite}
            isRewriting={false}
          />
          <OpenDoorResearchSection companyName={companyName} />
        </Box>
      </Box>
    </Box>
  );
};

export default ClientWorkspaceHandleReply;
